import React, { useState, useEffect, useRef } from "react";
import Calendar from "./Calendar";

type DateTimePickerProps = {
	value: string;
	onChange: (value: string) => void;
	label: string;
	id: string;
	required?: boolean;
	placeholder?: string;
	dateOnly?: boolean; // 日付のみモード (時間なし)
	disabled?: boolean;
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
	value,
	onChange,
	label,
	id,
	required = false,
	placeholder = "日時を選択してください",
	dateOnly = false,
	disabled = false,
}) => {
	const [showPicker, setShowPicker] = useState(false);
	const [localDate, setLocalDate] = useState<Date | null>(
		value ? new Date(value) : null
	);
	const [calendarDate, setCalendarDate] = useState(new Date());
	const [selectedHour, setSelectedHour] = useState("00");
	const [selectedMinute, setSelectedMinute] = useState("00");
	const pickerRef = useRef<HTMLDivElement>(null);

	// 初期値をセットアップ
	useEffect(() => {
		if (value) {
			const dateObj = new Date(value);
			setLocalDate(dateObj);
			setSelectedHour(String(dateObj.getHours()).padStart(2, "0"));
			setSelectedMinute(String(dateObj.getMinutes()).padStart(2, "0"));
		}
	}, [value]);

	// クリックイベントのハンドラーでピッカーの外側をクリックしたら閉じる
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				pickerRef.current &&
				!pickerRef.current.contains(event.target as Node)
			) {
				setShowPicker(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (disabled) {
			setShowPicker(false);
		}
	}, [disabled]);

	// 日付をフォーマットする関数
	const formatDateTimeToJapanese = (
		date: Date,
		hour: string,
		minute: string
	): string => {
		// dateOnlyモードの場合は時間を含めない
		if (dateOnly) {
			return `${date.getFullYear()}年${
				date.getMonth() + 1
			}月${date.getDate()}日`;
		}
		return `${date.getFullYear()}年${
			date.getMonth() + 1
		}月${date.getDate()}日 ${hour}:${minute}`;
	};

	// 日付を選択する
	const handleDateSelect = (year: number, month: number, day: number) => {
		const newDate = new Date(year, month, day);
		setLocalDate(newDate);

		if (dateOnly) {
			// 日付のみモードの場合は正午(12:00)に固定する
			newDate.setHours(12);
			newDate.setMinutes(0);
		} else {
			// 通常モードでは時間情報を保持
			newDate.setHours(parseInt(selectedHour));
			newDate.setMinutes(parseInt(selectedMinute));
		}

		onChange(newDate.toISOString());
	};

	// 時間を選択する
	const handleTimeChange = (hour: string, minute: string) => {
		setSelectedHour(hour);
		setSelectedMinute(minute);

		// 日付が選択されている場合のみ時間を適用
		if (localDate) {
			const newDate = new Date(localDate);
			newDate.setHours(parseInt(hour));
			newDate.setMinutes(parseInt(minute));
			onChange(newDate.toISOString());
		}
	};

	// 日時の適用ボタンをクリック
	const handleApply = () => {
		if (localDate) {
			setShowPicker(false);
		}
	};

	// 表示用のフォーマットされた日時
	const displayValue = localDate
		? formatDateTimeToJapanese(localDate, selectedHour, selectedMinute)
		: "";

	const handleOpenPicker = () => {
		if (disabled) return;
		setShowPicker(true);
	};

	return (
		<div className="relative">
			<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
				{label}{" "}
				{required && <span className="text-red-500 dark:text-red-400">*</span>}
			</label>
			<input
				type="text"
				id={id}
				value={displayValue}
				onClick={handleOpenPicker}
				readOnly
				placeholder={placeholder}
				disabled={disabled}
				className={`w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors ${
					disabled
						? "cursor-not-allowed bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-500"
						: "cursor-pointer"
				}`}
			/>
			<button
				type="button"
				onClick={handleOpenPicker}
				disabled={disabled}
				className={`absolute inset-y-0 right-0 px-3 mt-6 flex items-center ${
					disabled ? "cursor-not-allowed text-gray-400" : ""
				}`}
			>
				<svg
					className={`w-5 h-5 ${
						disabled ? "text-gray-400" : "text-gray-500 dark:text-gray-400"
					}`}
					fill="currentColor"
					viewBox="0 0 20 20"
				>
					<path
						fillRule="evenodd"
						d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
						clipRule="evenodd"
					/>
				</svg>
			</button>

			{showPicker && !disabled && (
				<div
					ref={pickerRef}
					className="absolute z-10 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg"
				>
					<div className="w-80 flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-xl overflow-hidden">
						{/* Calendar */}
						<Calendar
							calendarDate={calendarDate}
							setCalendarDate={setCalendarDate}
							localDate={localDate}
							handleDateSelect={handleDateSelect}
						/>
						{/* Time Selection - date-only モードでは非表示 */}
						{!dateOnly && (
							<div className="pt-3 px-3 flex justify-center items-center gap-x-4 border-t border-gray-200 dark:border-gray-700">
								<div className="flex items-center">
									<select
										value={selectedHour}
										onChange={(e) =>
											handleTimeChange(e.target.value, selectedMinute)
										}
										className="w-16 py-1 px-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-violet-500 focus:ring-violet-500"
									>
										{Array.from({ length: 24 }, (_, i) =>
											String(i).padStart(2, "0")
										).map((hour) => (
											<option key={hour} value={hour}>
												{hour}
											</option>
										))}
									</select>
									<span className="mx-1 text-gray-700 dark:text-gray-300">
										:
									</span>
									<select
										value={selectedMinute}
										onChange={(e) =>
											handleTimeChange(selectedHour, e.target.value)
										}
										className="w-16 py-1 px-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-violet-500 focus:ring-violet-500"
									>
										{Array.from({ length: 12 }, (_, i) =>
											String(i * 5).padStart(2, "0")
										).map((minute) => (
											<option key={minute} value={minute}>
												{minute}
											</option>
										))}
									</select>
								</div>
							</div>
						)}

						{/* Button Group */}
						<div className="py-3 px-4 flex items-center justify-end gap-x-2 border-t border-gray-200 mt-3">
							<button
								type="button"
								onClick={() => setShowPicker(false)}
								className="py-2 px-3 inline-flex items-center text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50"
							>
								キャンセル
							</button>
							<button
								type="button"
								onClick={handleApply}
								className="py-2 px-3 inline-flex justify-center items-center text-xs font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700"
							>
								適用
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DateTimePicker;
